import React, { useEffect } from 'react';
import ReactPlayer from 'react-player';
import ChannelService from '../../api/ChannelService';
import HFbutton from '../../components/button/HFbutton';
import { Spin } from 'antd';
import { useContentsData } from './hooks/useContentsData';
import { useFullscreen } from './hooks/useFullscreen';
import { usePlayerToken } from './hooks/usePlayerToken';
import { useContentsMessage } from './hooks/useContentsMessage';

/**
 * 콘텐츠 뷰어 팝업('/contents') 페이지.
 * @description
 * - 오프너 창이 postMessage로 보낸 데이터(contentsView/todoView/learningView/closeEvent)를 받아
 *   해당 콘텐츠를 iframe으로 렌더링한다. (수신 처리: useContentsMessage)
 * - 젭스플레이어(과제) 콘텐츠에는 accessToken을 postMessage로 주입한다. (usePlayerToken)
 * - 콘텐츠 데이터는 세션 스토리지에 저장되어 새로고침 시 복원된다. (useContentsData)
 */
const ContentsView = () => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const { contentsData, saveContentsData } = useContentsData();
  const { possibleFullScreen, isFullScreen, enterFullScreen } = useFullscreen();
  const { setTodoAccessToken, postTokenToPlayer, postTokenToPlayerRef } =
    usePlayerToken(contentsData, iframeRef);

  useContentsMessage({
    iframeRef,
    saveContentsData,
    setTodoAccessToken,
    postTokenToPlayerRef,
  });

  // 채널톡 버튼: 마운트 시 숨기고 언마운트 시 복원
  useEffect(() => {
    ChannelService.hideChannelButton();
    return () => ChannelService.showChannelButton();
  }, []);

  // 로딩 상태
  if (contentsData === undefined) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  // Vimeo, 학생할일(todoView)이 아닐 때만 전체화면 버튼 표시
  const visibleFullScreenBtn =
    possibleFullScreen &&
    !isFullScreen &&
    contentsData.contentsType !== 'Vimeo' &&
    contentsData.contentsType !== 'todoView';

  return (
    <div className="h-screen w-screen">
      {!contentsData.contentsUrl ? (
        <div>비정상적인 접근입니다.</div>
      ) : contentsData.contentsType === 'Vimeo' ? (
        // Vimeo는 iframe/젭스플레이어가 아닌 ReactPlayer로 직접 재생한다.
        <div className="flex h-screen w-screen items-center justify-center bg-black p-4">
          <ReactPlayer
            url={contentsData.contentsUrl}
            width="100%"
            height="100%"
            controls
          />
        </div>
      ) : (
        <>
          <iframe
            ref={iframeRef}
            src={contentsData.contentsUrl}
            width={'100%'}
            height={'100%'}
            allowFullScreen
            allow="microphone"
            title="contents-view"
            onLoad={postTokenToPlayer}
          />
          {visibleFullScreenBtn && (
            <div className="fixed right-1 top-1 h-10 w-20">
              <HFbutton height={36} onClick={enterFullScreen}>
                전체화면
              </HFbutton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContentsView;
